import { createGlobalStyle } from '@xstyled/styled-components';
import blueJones from '~/assets/fonts/blue-jones.woff2';
import raleway from '~/assets/fonts/Raleway-Regular.woff2';
import recoleta from '~/assets/fonts/Recoleta-Regular.woff2';
import ralewayBold from '~/assets/fonts/Raleway-Bold.woff2';
import recoletaBold from '~/assets/fonts/Recoleta-SemiBold.woff2';
import plusJakartaSans from '~/assets/fonts/PlusJakartaSans.woff2';
import plusJakartaSansExt from '~/assets/fonts/PlusJakartaSans-ext.woff2';

export const MainStyle = createGlobalStyle`
  @font-face {
    font-family: bluejones;
    src: url(${blueJones});
  }

  @font-face {
    font-family: raleway;
    src: url(${raleway});
  }

  @font-face {
    font-family: recoleta;
    src: url(${recoleta});
  }

  @font-face {
    font-family: ralewayBold;
    src: url(${ralewayBold});
  }

  @font-face {
    font-family: recoletaBold;
    src: url(${recoletaBold});
  }

  @font-face {
    font-family: 'Plus Jakarta Sans';
    font-style: normal;
    font-weight: 200 800;
    font-display: swap;
    src: url(${plusJakartaSans}) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }

  @font-face {
    font-family: 'Plus Jakarta Sans';
    font-style: normal;
    font-weight: 200 800;
    font-display: swap;
    src: url(${plusJakartaSansExt}) format('woff2');
    unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }

  :root {
    --color-black: #000000;
    --color-white: #ffffff;

    --color-grey-dk: #6f6f6f;
    --color-grey-md: #e0e0e0;
    --color-grey-lt: #f5f5f5;

    --color-accent-dk: #9845FB;
    --color-accent-md: #9D76FB;
    --color-accent-lt: #F1EEFE;

    /* CAT-1261: companions for brand fill / focus / links (overridden in JS) */
    --color-on-accent: #ffffff;
    --color-accent-fg: var(--color-accent-dk);
    --color-accent-on-light: var(--color-accent-dk);
    --color-focus-ring: var(--color-accent-dk);
    --color-focus-ring-inner: #ffffff;
    --color-accent-edge: transparent;

    --color-yellow-dk: #FDD15F;
    --color-yellow-md: #F8EAB7;
    --color-yellow-lt: #FCF8E9;

    --color-green-dk: #398500;
    --color-green-md: #92E8BF;
    --color-green-lt: #E8FAED;

    --color-red-dk: #D81242;
    --color-red-md: #FABCCB;
    --color-red-lt: #FDF2F4;
    
    --color-blue-md: #0975FD;

    --color-pink-lt: #F3ECFF;
    --color-pink-lt2: #F3ECFF;

    --title-font: recoleta;
    --body-font: raleway;
    --llama-font: bluejones;

    /* EDS CAT scrollbar — track transparent, thumb 4px inset in 6px gutter */
    --scrollbar-track: transparent;
    --scrollbar-thumb: #a9a9a9;
  }

  /* Firefox */
  * {
    scrollbar-width: thin;
    scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
  }

  /* Chrome, Safari, Edge */
  *::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    background: transparent;
  }

  *::-webkit-scrollbar-track,
  *::-webkit-scrollbar-track-piece {
    background: transparent;
  }

  *::-webkit-scrollbar-thumb {
    background-color: var(--scrollbar-thumb);
    border-radius: 999px;
    border: 1px solid transparent;
    background-clip: content-box;
  }

  *::-webkit-scrollbar-corner {
    background: transparent;
  }

  html, body, .root, #root {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    background-color: var(--color-white);
  }

  a {
    text-decoration: none;
    color: var(--color-accent-fg);
  }

  p {
    margin: 0;
  }

  button {
    cursor: pointer;
  }

  /* UA button:focus has higher specificity than bare :focus-visible;
     target element selectors so primary outline wins over the blue ring. */
  a:focus,
  button:focus,
  input:focus,
  textarea:focus,
  select:focus,
  summary:focus,
  [tabindex]:focus {
    outline: none;
  }

  a:focus-visible,
  button:focus-visible,
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible,
  summary:focus-visible,
  [tabindex]:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  textarea {
    border: none;
    background: var(--color-grey-lt);
    border-radius: md;
  }

  video::-webkit-media-controls {
    display: none;
  }
  
  .dontForget {
      background: var(--color-accent-dk);
      color: var(--color-on-accent);
      padding: 5px;
      padding-left: 10px;
      padding-right: 10px;
      opacity: 0.7;
      border-radius: 10px;
  }

  .subtitleStyleForDarkTheme {
    padding: 5px 8px;
    width: fit-content;
    background-color: #FAFEFB;
    border-radius: 8px;
    
    svg {
      position: relative;
    }
  }

  .text-align-left {
    text-align: left !important;
  }

  .text-align-center {
    text-align: center !important;
  }

  .text-align-right {
    text-align: right !important;
  }

  .text-align-justify {
    text-align: justify !important;
  }

  .navigationStyleForDarkTheme {
    background-color: var(--color-white);
  }

  .textareaStyleForDarTheme {
    border: 1px solid var(--color-grey-dk);
  }
`;
